import { getHumanUserRole } from '@/utils/UserUtil';
import config from '@/plugins/bluejay/config';
import momentUtil from '@/utils/MomentUtil';
import { apiRequest } from './ApiUtil';

export {
  resultsModelMap,
  search,
};

const resultsModelMap = {
  user: {
    icon: 'fas fa-user',
    color: 'red',
    label: (item) => item.full_name,
    sublabel: getUserSubLabel,
    stamp: (item) => getHumanUserRole(item.role),
    handler: (item) => {
      const role = item.role;
      if ([config.roles.ACCOUNT_OWNER, config.roles.MEMBER].includes(role)) {
        return {
          name: 'app.businesses.profile',
          params: { id: item.business_id },
        };
      }
      return {
        name: 'app.agents',
        params: { id: item.id },
      };
    },
  },
  lead: {
    icon: 'pages',
    color: 'purple',
    label: (item) => item.full_name,
    stamp: () => 'Lead',
    sublabel: getLeadSublabel,
    handler: (item) => ({
      name: 'app.leads.profile',
      params: { id: item.id },
    }),
  },
  property: {
    icon: 'fas fa-building',
    color: 'pink',
    label: getPropertyLabel,
    stamp: () => 'Property',
    sublabel: getPropertySublabel,
    sublabelLines: 2,
    handler: (item) => ({
      name: 'app.properties.combined',
      params: { id: item.id },
    }),
  },
  business: {
    icon: 'business_center',
    color: 'green',
    label: (item) => item.name || item.owner_name,
    stamp: () => 'Business',
    sublabel: (item) => `#${item.id}`,
    handler: (item) => ({
      name: 'app.businesses.profile',
      params: { id: item.id },
    }),
  },
  tour: {
    icon: 'fa fa-sign',
    color: 'green',
    label: (item) => String(item.id),
    sublabel: getTourSublabel,
    stamp: () => 'Tour',
    handler: (item) => ({
      name: 'app.tours',
      params: { id: item.id },
    }),
  },
  inspection: {
    icon: 'credit_card',
    color: 'green',
    label: (item) => String(item.id),
    sublabel: getInspectionSublabel,
    stamp: () => 'Inspection',
    handler: (item) => ({
      name: 'app.inspections',
      params: { id: item.id },
    }),
  },
};

function getUserSubLabel(item) {
  const sublabel = [];
  if ([config.roles.ACCOUNT_OWNER, config.roles.MEMBER].includes(item.role)) {
    if (item.business && item.business.name) {
      sublabel.push(item.business.name);
    }
  }

  if ([config.roles.AGENT].includes(item.role)) {
    if (item.is_showami) {
      sublabel.push('<span class="font-weight-bold text-warning">Showami</span>');
    }
    if (item.is_tours_experienced) {
      sublabel.push('<span class="font-weight-bold">Tours Experienced</span>');
    }
    if (item.is_inspections_experienced) {
      sublabel.push('<span class="font-weight-bold">Inspections Experienced</span>');
    }
    if (item.markets && item.markets.length) {
      sublabel.push(item.markets.map((market) => market.name).join(', '));
    }
  }
  sublabel.push(`#${item.id}`);
  return sublabel.join(' | ');
}

function getPropertyLabel(item) {
  let label = item.short_address;
  if (item.units && item.units.length === 1 && item.units[0].name) {
    label += ` ${item.units[0].name}`;
  }
  return label;
}

function getPropertySublabel(item) {
  const sublabel = [`<span class="font-weight-bold">${item.business.name}</span>`];
  if (item.property_type) {
    sublabel.push(item.property_type);
  }

  if (item.units && item.units.length === 1) {
    if (item.units[0].showing_enabled) {
      sublabel.push('<span class="text-green">Showing</span>');
    }
  } else {
    sublabel.push(`${item.units.length} units`);
    const showingUnitsNames = item.units.filter((unit) => unit.showing_enabled).map((unit) => unit.name);
    if (showingUnitsNames.length) {
      sublabel.push(`<span class="text-green">Showing: ${showingUnitsNames
        .join(', ')}</span>`);
    }
  }

  sublabel.push(`<i>#${item.id}</i>`);
  return sublabel.join(' | ');
}

async function search(query, method, filters) {
  try {
    const searchResults = await getSearchResults(query, method, filters);
    const items = [];

    searchResults.hits.forEach((result) => {
      const resultType = resultsModelMap[result.model];
      const {
        label, handler, icon, color, stamp, sublabel,
      } = resultType;
      items.push({
        label: label(result),
        stamp: stamp(result),
        sublabel: sublabel(result),
        value: label(result),
        data: result,
        handler,
        icon,
        leftColor: color,
      });
    });
    return items;
  } catch (error) {
    return [];
  }
}

async function getSearchResults(rawQuery, method, filters) {
  const query = cleanQuery(rawQuery);
  return apiRequest('POST', 'admin/search', {
    method,
    query,
    filters,
  });
}

function cleanQuery(rawQuery) {
  // if raw query matches phone number pattern, remove all non-digit characters except + sign
  const phoneNumberRegex = config.phoneNumberRegex;
  if (rawQuery.length >= 8 && phoneNumberRegex.test(rawQuery)) {
    return rawQuery.replace(/[^+\d]+/g, '');
  }
  return rawQuery;
}

function getTourSublabel(item) {
  const sublabel = [];
  sublabel.push(`<span class="font-weight-bold">${momentUtil(item.tour_at, item.property.timezone).toDisplayFormat()}</span>`);
  sublabel.push(item.status);
  if (item.user) {
    sublabel.push(`Agent: ${item.user.full_name}`);
  }
  if (item.property) {
    let address = item.property.short_address;
    if (item.unit && item.unit.name) {
      address += ' - item.unit.name';
    }
    sublabel.push(address);
  }

  if (item.property && item.property.business && item.property.business.name) {
    sublabel.push(item.property.business.name);
  }
  sublabel.push(`<i>#${item.id}</i>`);
  return sublabel.join(' | ');
}

function getInspectionSublabel(item) {
  const sublabel = [];
  sublabel.push(`<span class="font-weight-bold">${momentUtil(item.preferred_from, item.property.timezone).toDisplayFormat()}</span>`);
  sublabel.push(item.type);
  sublabel.push(item.state);
  if (item.property) {
    let address = item.property.short_address;
    if (item.unit && item.unit.name) {
      address += ' - item.unit.name';
    }
    sublabel.push(address);
  }

  if (item.property && item.property.business) {
    sublabel.push(item.property.business.name);
  }
  sublabel.push(`<i>#${item.id}</i>`);
  return sublabel.join(' | ');
}

function getLeadSublabel(item) {
  const sublabel = [];
  if (item.is_blocked) {
    sublabel.push('<span class="text-red">Blocked</span>');
  }
  if (item.formatted_phone_number) {
    sublabel.push(item.formatted_phone_number);
  }
  if (sublabel.email) {
    sublabel.push(item.email);
  }

  sublabel.push(`#${item.id}`);
  return sublabel.join(' | ');
}
